import pick from 'lodash/pick';
import moment from 'moment';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  transactionLineItems,
  fetchTransactions as fetchTransactionsApi,
  saveLastMessageAt,
} from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { TRANSITION_ENQUIRE } from '../../util/customTransaction/mainRentalTransaction';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  parse,
} from '../../util/urlHelpers';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import get from 'lodash/get';
import { daysBetween } from '../../util/dates';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ListingPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ListingPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ListingPage/SEND_ENQUIRY_ERROR';

export const FETCH_REVIEW_DIRECTLY_REQUEST = 'app/ListingPage/FETCH_REVIEW_DIRECTLY_REQUEST';
export const FETCH_REVIEW_DIRECTLY_SUCCESS = 'app/ListingPage/FETCH_REVIEW_DIRECTLY_SUCCESS';
export const FETCH_REVIEW_DIRECTLY_ERROR = 'app/ListingPage/FETCH_REVIEW_DIRECTLY_ERROR';

export const FETCH_TRANSACTIONS_REQUEST = 'app/ListingPage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/ListingPage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/ListingPage/FETCH_TRANSACTIONS_ERROR';
export const CLEAR_TRANSACTIONS = 'app/ListingPage/CLEAR_TRANSACTIONS';

export const UPDATE_TRANSACTION_CURRENT_PAGE = 'app/ListingPage/UPDATE_TRANSACTION_CURRENT_PAGE';

export const FETCH_ALL_OWNER_REVIEWS_REQUEST = 'app/ListingPage/FETCH_ALL_OWNER_REVIEWS_REQUEST';
export const FETCH_ALL_OWNER_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_ALL_OWNER_REVIEWS_SUCCESS';
export const FETCH_ALL_OWNER_REVIEWS_ERROR = 'app/ListingPage/FETCH_ALL_OWNER_REVIEWS_ERROR';

// ================ Reducer ================ //
const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendEnquiryInProgress: false,
  sendEnquiryError: null,
  enquiryModalOpenForListingId: null,
  averageRating: 0,
  totalReviewRating: 0,
  fetchReviewsDirectlyInProgress: false,
  fetchReviewsDirectlyError: null,
  // transactions of listing
  transactionRefs: [],
  fetchTransactionsInProgress: false,
  fetchTransactionsError: null,
  pagination: null,
  currentPage: 1,

  allOwnerReviews: null,
  totalReviews: 0,
  fetchAllOwnerReviewsInProgress: false,
  fetchAllOwnerReviewsError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST:
      return { ...state, fetchTimeSlotsError: null };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, timeSlots: payload };
    case FETCH_TIME_SLOTS_ERROR:
      return { ...state, fetchTimeSlotsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryInProgress: false };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };

    case FETCH_REVIEW_DIRECTLY_REQUEST:
      return { ...state, fetchReviewsDirectlyInProgress: true, fetchReviewsDirectlyError: null };
    case FETCH_REVIEW_DIRECTLY_SUCCESS:
      return {
        ...state,
        fetchReviewsDirectlyInProgress: false,
        averageRating: payload.averageRating,
        totalReviewRating: payload.totalReviewRating,
      };
    case FETCH_REVIEW_DIRECTLY_ERROR:
      return {
        ...state,
        fetchReviewsDirectlyInProgress: false,
        fetchReviewsDirectlyError: payload,
      };

    case FETCH_TRANSACTIONS_REQUEST:
      return {
        ...state,
        fetchTransactionsInProgress: true,
        fetchTransactionsError: null,
      };

    case FETCH_TRANSACTIONS_SUCCESS: {
      const { transactionRefs: transactionRefsPayload, pagination } = payload;
      const { transactionRefs } = state;
      return {
        ...state,
        fetchTransactionsInProgress: false,
        transactionRefs: [...transactionRefs, ...transactionRefsPayload],
        pagination: pagination,
      };
    }
    case FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchTransactionsInProgressfalse: false,
        fetchTransactionsError: payload,
      };
    case CLEAR_TRANSACTIONS:
      return {
        ...state,
        transactionRefs: [],
        pagination: null,
        currentPage: 1,
      };

    case UPDATE_TRANSACTION_CURRENT_PAGE: {
      const { currentPage } = state;
      return { ...state, currentPage: currentPage + 1 };
    }

    case FETCH_ALL_OWNER_REVIEWS_REQUEST:
      return { ...state, fetchAllOwnerReviewsInProgress: true, fetchAllOwnerReviewsError: null };
    case FETCH_ALL_OWNER_REVIEWS_SUCCESS:
      return {
        ...state,
        allOwnerReviews: payload.reviews,
        totalReviews: payload.totalReviews,
        fetchAllOwnerReviewsInProgress: false,
      };
    case FETCH_ALL_OWNER_REVIEWS_ERROR:
      return {
        ...state,
        fetchAllOwnerReviewsInProgress: false,
        fetchAllOwnerReviewsError: payload,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = () => ({ type: FETCH_TIME_SLOTS_REQUEST });
export const fetchTimeSlotsSuccess = timeSlots => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: timeSlots,
});
export const fetchTimeSlotsError = error => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = () => ({ type: SEND_ENQUIRY_SUCCESS });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });

export const fetchReviewDirectlyRequest = () => ({ type: FETCH_REVIEW_DIRECTLY_REQUEST });
export const fetchReviewDirectlySuccess = data => ({
  type: FETCH_REVIEW_DIRECTLY_SUCCESS,
  payload: data,
});
export const fetchReviewDirectlyError = e => ({
  type: FETCH_REVIEW_DIRECTLY_ERROR,
  error: true,
  payload: e,
});

export const fetchTransactionsRequest = () => ({ type: FETCH_TRANSACTIONS_REQUEST });
const fetchTransactionsSuccess = (transactionRefs, pagination) => ({
  type: FETCH_TRANSACTIONS_SUCCESS,
  payload: {
    transactionRefs,
    pagination,
  },
});
const fetchTransactionsError = e => ({
  type: FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

const clearTransactions = () => ({
  type: CLEAR_TRANSACTIONS,
});

export const loadSeeMore = () => ({
  type: UPDATE_TRANSACTION_CURRENT_PAGE,
});

export const fetchAllOwnerReviewsRequest = () => ({
  type: FETCH_ALL_OWNER_REVIEWS_REQUEST,
});
export const fetchAllOwnerReviewsSuccess = (reviews, totalReviews) => ({
  type: FETCH_ALL_OWNER_REVIEWS_SUCCESS,
  payload: { reviews, totalReviews },
});
export const fetchAllOwnerReviewsError = error => ({
  type: FETCH_ALL_OWNER_REVIEWS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const showListing = (listingId, isOwn = false) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Image carousel
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const response = denormalisedResponseEntities(data)[0];
      dispatch(addMarketplaceEntities(data));
      return response;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchReviewsDirectly = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewDirectlyRequest());

  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const averageRating = entities.reduce((acc, curr, index) => {
        return (acc + curr.attributes.rating) / (index + 1);
      }, 0);
      fetchReviewDirectlySuccess({
        averageRating: parseFloat(averageRating).toFixed(2),
        totalReviewRating: response.length,
      });
    })
    .catch(e => dispatch(fetchReviewDirectlyError(storableError(e))));
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const fetchAllOwnerReviews = userId => (dispatch, getState, sdk) => {
  dispatch(fetchAllOwnerReviewsRequest());
  return sdk.reviews
    .query({ subject_id: userId, state: 'public' })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      const totalReviews = get(response, 'data.meta.totalItems', 0);
      dispatch(fetchAllOwnerReviewsSuccess(reviews, totalReviews));
    })
    .catch(e => {
      dispatch(fetchAllOwnerReviewsError(storableError(e)));
    });
};

export const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  // Time slots can be fetched for 90 days at a time,
  // for at most 180 days from now. If max number of bookable
  // day exceeds 90, a second request is made.
  const maxTimeSlots = 90;

  const { listingId, start, end } = params;

  const allQueryParams = [];
  let startDate = start;
  let endDate = end;

  if (daysBetween(startDate, endDate) > 90) {
    while (daysBetween(startDate, endDate) > 90) {
      const newEndDate = moment(startDate)
        .utc()
        .startOf('day')
        .add(maxTimeSlots, 'days')
        .toDate();
      const timeSlotsParams = {
        listingId,
        start: startDate,
        end: newEndDate,
      };

      allQueryParams.push(timeSlotsParams);
      startDate = newEndDate;
    }

    allQueryParams.push({ listingId, start: startDate, end: endDate });
  }

  if (allQueryParams.length > 0) {
    const listTask = allQueryParams.map(param => sdk.timeslots.query(param));
    return Promise.all(listTask).then(responses => {
      const allTimeSlots = responses.reduce((result, item) => {
        const denormalisedItem = denormalisedResponseEntities(item);
        const newResult = result.concat(denormalisedItem);
        return newResult;
      }, []);

      return allTimeSlots;
    });
  }

  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchTimeSlotsRequest);

  // Time slots can be fetched for 90 days at a time,
  // for at most 180 days from now. If max number of bookable
  // day exceeds 90, a second request is made.

  const maxTimeSlots = 90;
  // booking range: today + bookable days -1
  const bookingRange = config.dayCountAvailableForFetching - 1;
  const timeSlotsRange = Math.min(bookingRange, maxTimeSlots);

  let start = moment()
    .utc()
    .startOf('day')
    .toDate();
  const end = moment(start)
    .utc()
    .add(timeSlotsRange, 'days')
    .toDate();
  const localTime = moment()
    .startOf('day')
    .toDate();

  // If local pass to the next day before UTC
  // using the next day of UTC
  if (localTime.getDate() !== start.getDate() && localTime.getTime() > start.getTime())
    start = moment()
      .utc()
      .startOf('day')
      .add(1, 'days')
      .toDate();

  const params = { listingId, start, end };

  return dispatch(timeSlotsRequest(params))
    .then(timeSlots => {
      const secondRequest = bookingRange > maxTimeSlots;

      if (secondRequest) {
        const secondRange = Math.min(maxTimeSlots, bookingRange - maxTimeSlots);
        const secondEnd = moment(end)
          .utc()
          .startOf('day')
          .add(secondRange, 'days')
          .toDate();
        const secondParams = {
          listingId,
          start: end,
          end: secondEnd,
        };

        return dispatch(timeSlotsRequest(secondParams)).then(secondBatch => {
          const secondCombined = timeSlots.concat(secondBatch);
          const thirdRequest = bookingRange > maxTimeSlots + secondRange;

          if (thirdRequest) {
            const thirdRange = Math.min(maxTimeSlots, bookingRange - maxTimeSlots - secondRange);
            const thirdEnd = moment(secondEnd)
              .utc()
              .startOf('day')
              .add(thirdRange, 'days')
              .toDate();
            const thirdParams = {
              listingId,
              start: secondEnd,
              end: thirdEnd,
            };

            return dispatch(timeSlotsRequest(thirdParams)).then(thirdBatch => {
              const thirdCombined = secondCombined.concat(thirdBatch);
              const fourthRequest = bookingRange > maxTimeSlots + secondRange + thirdRange;

              if (fourthRequest) {
                const fourthRange = Math.min(
                  maxTimeSlots,
                  bookingRange - maxTimeSlots - secondRange - thirdRange
                );
                const fourthEnd = moment(thirdEnd)
                  .utc()
                  .startOf('day')
                  .add(fourthRange, 'days')
                  .toDate();
                const fourthParams = {
                  listingId,
                  start: thirdEnd,
                  end: fourthEnd,
                };

                return dispatch(timeSlotsRequest(fourthParams)).then(fourthBatch => {
                  const fourthCombined = thirdCombined.concat(fourthBatch);
                  dispatch(fetchTimeSlotsSuccess(fourthCombined));
                });
              }
            });
          }
        });
      } else {
        dispatch(fetchTimeSlotsSuccess(timeSlots));
      }
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(storableError(e)));
    });
};

const saveLastMessageCreatedAt = transactionId => {
  return saveLastMessageAt({ transactionId })
    .then(() => {
      //do nothing
    })
    .catch(err => {
      log.error(err, 'save-last-message-created-at');
    });
};

export const sendEnquiry = (listingId, message) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());

  const { currentUser } = getState().user;
  const { isInstantBooking = false } = get(currentUser, 'attributes.profile.metadata', {});
  const processAlias = isInstantBooking
    ? config.instantBookingProcessAlias
    : config.bookingProcessAlias;

  const bodyParams = {
    transition: TRANSITION_ENQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        saveLastMessageCreatedAt(transactionId.uuid);
        dispatch(sendEnquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({ bookingData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  return transactionLineItems({ bookingData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
      return response;
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        bookingData: bookingData,
      });
    });
};

export const fetchTransactions = (listingId, authorId) => async (dispatch, getState, sdk) => {
  dispatch(fetchTransactionsRequest());

  const { currentPage } = getState().ListingPage;

  return fetchTransactionsApi({ listingId, providerId: authorId, currentPage })
    .then(response => {
      const transactions = denormalisedResponseEntities(response);
      const transactionRefs = entityRefs(transactions);
      const pagination = get(response, 'data.meta');

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchTransactionsSuccess(transactionRefs, pagination));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionsError(storableError(e)));
    });
};

export const loadData = params => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, true));
  }

  if (config.enableAvailability) {
    return Promise.all([
      dispatch(showListing(listingId)),
      dispatch(fetchReviewsDirectly(listingId)),
      dispatch(fetchTimeSlots(listingId)),
      dispatch(fetchReviews(listingId)),
      dispatch(clearTransactions()),
      dispatch(fetchCurrentUser()),
    ]).then(response => {
      const listingRes = response.find(res => res?.type === 'listing');
      const authorId = get(listingRes, 'author.id.uuid', null);
      const { currentUser } = getState().user;
      if (currentUser?.id?.uuid === authorId) {
        dispatch(fetchTransactions(listingId, authorId));
      }

      return dispatch(fetchAllOwnerReviews(authorId));
    });
  } else {
    return Promise.all([dispatch(showListing(listingId)), dispatch(fetchReviews(listingId))]).then(
      response => {
        const listingRes = response.find(res => res.type === 'listing');
        const authorId = get(listingRes, 'author.id.uuid', null);
        return dispatch(fetchAllOwnerReviews(authorId));
      }
    );
  }
};
